/*
 * GLOST newsletter sign-up and promo pop-up.
 * Markup: snippets/glost-signup-form.liquid, sections/glost-signup.liquid,
 * sections/glost-promo-popup.liquid. Loaded as a module, so it runs once however many
 * forms are on the page.
 */

const PREFIX = 'glost:';
const DAY = 24 * 60 * 60 * 1000;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/* Browser storage can be missing or throw (private windows, blocked site data). Never fatal. */
const store = {
  get(key) {
    try {
      return window.localStorage.getItem(PREFIX + key);
    } catch (error) {
      return null;
    }
  },
  set(key, value) {
    try {
      window.localStorage.setItem(PREFIX + key, value);
    } catch (error) {
      /* nothing to do */
    }
  },
};

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* Mailchimp messages can carry HTML, such as a profile link. Show them as plain text. */
const plainText = (html) => {
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  return (doc.body.textContent || '').replace(/^\s*\d+\s*-\s*/, '').trim();
};

class GlostSignup extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.ready = true;

    this.email = this.form.querySelector('[data-signup-email]');
    this.privacy = this.form.querySelector('[data-signup-privacy]');
    this.status = this.form.querySelector('[data-signup-status]');
    this.submitButton = this.form.querySelector('[type="submit"]');

    // The browser's own bubbles are replaced by the messages below
    this.form.noValidate = true;
    // Capture phase on the wrapper, so this runs before anything listening on the form or the
    // document. Shopify's hCaptcha (bound to every /contact customer form on first focus) adds
    // its own submit listeners that post the form after the captcha whether or not the event
    // was cancelled, so an invalid submit has to be stopped here, before it reaches them.
    this.addEventListener('submit', (event) => this.onSubmit(event), true);
    this.form.addEventListener('focusin', () => this.classList.add('is-expanded'));
    this.email.addEventListener('input', () => this.clearError(this.email));
    this.privacy.addEventListener('change', () => this.clearError(this.privacy));

    this.showPostedResult();
  }

  /* Shopify mode: after the page comes back from /contact, show the result in the form that
     was sent. The form's id comes back as the URL fragment; without it, the footer answers. */
  showPostedResult() {
    const posted = this.status && this.status.dataset.posted;
    if (!posted) return;
    const hash = window.location.hash.slice(1);
    const sentHere = hash ? hash === this.form.id : this.dataset.context === 'footer';
    if (!sentHere) {
      this.status.textContent = '';
      return;
    }
    if (posted === 'success') {
      this.classList.add('is-done');
      store.set('subscribed', String(Date.now()));
    }
  }

  get messages() {
    return {
      required: this.dataset.msgRequired,
      invalid: this.dataset.msgInvalid,
      privacy: this.dataset.msgPrivacy,
      already: this.dataset.msgAlready,
      failed: this.dataset.msgFailed,
    };
  }

  errorFor(input) {
    const id = (input.getAttribute('aria-describedby') || '').split(' ')[0];
    return id ? document.getElementById(id) : null;
  }

  setError(input, message) {
    const error = this.errorFor(input);
    input.setAttribute('aria-invalid', 'true');
    if (error) {
      error.textContent = message;
      error.hidden = false;
    }
  }

  clearError(input) {
    const error = this.errorFor(input);
    input.removeAttribute('aria-invalid');
    if (error) {
      error.textContent = '';
      error.hidden = true;
    }
  }

  validate() {
    const invalid = [];
    const value = this.email.value.trim();
    this.clearError(this.email);
    this.clearError(this.privacy);

    if (!value) {
      this.setError(this.email, this.messages.required);
      invalid.push(this.email);
    } else if (!EMAIL_PATTERN.test(value)) {
      this.setError(this.email, this.messages.invalid);
      invalid.push(this.email);
    }
    if (!this.privacy.checked) {
      this.setError(this.privacy, this.messages.privacy);
      invalid.push(this.privacy);
    }
    if (invalid.length) invalid[0].focus();
    return invalid.length === 0;
  }

  onSubmit(event) {
    if (event.target !== this.form) return;
    const stop = () => {
      event.preventDefault();
      event.stopPropagation();
    };
    this.showStatus('');
    if (this.busy || !this.validate()) {
      stop();
      return;
    }
    this.email.value = this.email.value.trim();

    if (this.dataset.mode === 'mailchimp') {
      stop();
      this.sendToMailchimp();
      return;
    }

    // Shopify: the interests become customer tags, then the event carries on to Shopify's
    // hCaptcha, which posts the form once the check passes
    const tags = this.form.querySelector('[data-signup-tags]');
    if (tags) {
      const chosen = [...this.form.querySelectorAll('[data-signup-interest]:checked')].map((box) => box.value);
      tags.value = ['newsletter', `signup-${this.dataset.context}`, ...chosen].join(', ');
    }
    this.setBusy(true);
    // If the captcha is abandoned the page never reloads, so free the button again
    setTimeout(() => this.setBusy(false), 8000);
  }

  /* Mailchimp's JSONP endpoint answers cross-site, so the visitor never leaves the page */
  sendToMailchimp() {
    const base = (this.dataset.mcUrl || '').replace('/post?', '/post-json?');
    const callback = `glostMailchimp${Date.now()}${Math.floor(Math.random() * 1000)}`;
    const params = new URLSearchParams(new FormData(this.form));
    params.set('c', callback);

    const script = document.createElement('script');
    let timer;
    const cleanup = () => {
      clearTimeout(timer);
      delete window[callback];
      script.remove();
      this.setBusy(false);
    };

    window[callback] = (data) => {
      cleanup();
      this.onMailchimpReply(data || {});
    };
    script.onerror = () => {
      cleanup();
      this.showStatus(this.messages.failed);
    };
    timer = setTimeout(() => {
      cleanup();
      this.showStatus(this.messages.failed);
    }, 15000);

    this.setBusy(true);
    script.src = `${base}${base.includes('?') ? '&' : '?'}${params.toString()}`;
    document.head.appendChild(script);
  }

  onMailchimpReply(data) {
    const message = plainText(data.msg);
    if (data.result === 'success') {
      store.set('subscribed', String(Date.now()));
      this.classList.add('is-done');
      this.showStatus(message);
      return;
    }
    if (/already subscribed/i.test(message)) {
      this.showStatus(this.messages.already);
      return;
    }
    this.showStatus(message || this.messages.failed);
  }

  showStatus(message) {
    if (this.status) this.status.textContent = message;
  }

  setBusy(busy) {
    this.busy = busy;
    if (!this.submitButton) return;
    if (busy) this.submitButton.setAttribute('aria-disabled', 'true');
    else this.submitButton.removeAttribute('aria-disabled');
  }
}

class GlostPromoPopup extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-popup-panel]');
    if (!this.panel || this.ready) return;
    this.ready = true;

    this.sectionId = this.dataset.sectionId;
    this.key = `popup:${this.dataset.campaign || 'default'}`;
    this.delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
    this.hideDays = Math.max(1, Number(this.dataset.hideDays) || 14);
    this.designMode = Boolean(window.Shopify && window.Shopify.designMode);

    this.onKeydown = this.onKeydown.bind(this);
    this.querySelectorAll('[data-popup-close]').forEach((button) =>
      button.addEventListener('click', () => this.close(true))
    );

    // Theme editor: never opens by itself, shows while its section is selected
    if (this.designMode) {
      this.onSelect = (event) => {
        if (event.detail && event.detail.sectionId === this.sectionId) this.open(false);
      };
      this.onDeselect = (event) => {
        if (event.detail && event.detail.sectionId === this.sectionId) this.close(false);
      };
      document.addEventListener('shopify:section:select', this.onSelect);
      document.addEventListener('shopify:section:deselect', this.onDeselect);
      return;
    }

    // Coming back from a Shopify sign-up sent from the pop-up: open straight away with the answer
    const form = this.querySelector('form');
    const status = this.querySelector('[data-signup-status][data-posted]');
    if (form && status && window.location.hash === `#${form.id}`) {
      this.open(true);
      return;
    }

    if (this.suppressed()) return;
    this.timer = setTimeout(() => this.tryOpen(), this.delay);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    document.removeEventListener('keydown', this.onKeydown);
    if (this.onSelect) document.removeEventListener('shopify:section:select', this.onSelect);
    if (this.onDeselect) document.removeEventListener('shopify:section:deselect', this.onDeselect);
  }

  suppressed() {
    const closedAt = Number(store.get(this.key));
    if (closedAt && Date.now() - closedAt < this.hideDays * DAY) return true;
    // Someone who has already signed up is not asked again; pure promotions still show
    if (this.dataset.hasForm === 'true' && store.get('subscribed')) return true;
    return false;
  }

  /* Wait while the visitor is typing or has the bag, menu or search open */
  tryOpen() {
    const active = document.activeElement;
    const typing = active && active.matches && active.matches('input, textarea, select, [contenteditable="true"]');
    const otherDialog = document.querySelector('dialog[open]');
    if (typing || otherDialog) {
      this.timer = setTimeout(() => this.tryOpen(), 3000);
      return;
    }
    this.open(true);
  }

  open(moveFocus) {
    if (this.isOpen) return;
    this.isOpen = true;
    this.returnFocus = document.activeElement;
    this.panel.hidden = false;
    if (reducedMotion()) {
      this.panel.classList.add('is-open');
    } else {
      requestAnimationFrame(() => requestAnimationFrame(() => this.panel.classList.add('is-open')));
    }
    document.addEventListener('keydown', this.onKeydown);
    if (moveFocus) this.panel.focus({ preventScroll: true });
  }

  close(remember) {
    if (!this.isOpen) return;
    this.isOpen = false;
    clearTimeout(this.timer);
    if (remember && !this.designMode) store.set(this.key, String(Date.now()));

    const hadFocus = this.panel.contains(document.activeElement);
    const hide = () => {
      if (!this.isOpen) this.panel.hidden = true;
    };
    this.panel.classList.remove('is-open');
    if (reducedMotion()) hide();
    else setTimeout(hide, 300);

    document.removeEventListener('keydown', this.onKeydown);
    const back = this.returnFocus;
    if (hadFocus && back && back !== document.body && back.isConnected && typeof back.focus === 'function') {
      back.focus({ preventScroll: true });
    }
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    // Leave Escape to the bag or menu if one of those is open and has focus
    if (document.querySelector('dialog[open]') && !this.panel.contains(event.target)) return;
    this.close(true);
  }
}

if (!customElements.get('glost-signup')) customElements.define('glost-signup', GlostSignup);
if (!customElements.get('glost-promo-popup')) customElements.define('glost-promo-popup', GlostPromoPopup);
